
import { useState } from 'react';
import { Trash2, Eye } from 'lucide-react';
import { FaKey, FaRobot } from 'react-icons/fa';
import { FaEye, FaImages, FaRegFileVideo, FaVideo } from 'react-icons/fa6';
import { RxAvatar } from "react-icons/rx";
import { MdAudiotrack, MdOutlineKeyboardVoice } from 'react-icons/md';
import { WiDaySunny } from 'react-icons/wi';
import { ALL_OPENAI_MODELS } from '../../constants/openAIModels';


// Modern Preview Modal - Updated for video support
const PreviewModal = ({ isOpen, onClose, type, url, title }) => {
  if (!isOpen) return null;

  return (
    <>
      <style>{`
        .modal-backdrop-custom {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.75);
          backdrop-filter: blur(8px);
          z-index: 1050;
          animation: fadeIn 0.2s ease-in-out;
        }
        .modal-content-custom {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          max-width: 800px;
          width: 90%;
          background: white;
          border-radius: 20px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          z-index: 1051;
          animation: slideUp 0.3s ease-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { 
            transform: translate(-50%, -45%);
            opacity: 0;
          }
          to { 
            transform: translate(-50%, -50%);
            opacity: 1;
          }
        }
        .video-preview {
          width: 100%;
          max-height: 500px;
          border-radius: 10px;
        }
      `}</style>

      <div className="modal-backdrop-custom" onClick={onClose}>
        <div className="modal-content-custom" onClick={e => e.stopPropagation()}>
          <div
            className="d-flex align-items-center justify-content-between p-4 border-bottom bg-light"
            style={{ borderRadius: '20px 20px 0 0' }}
          >
            <h5 className="mb-0 fw-semibold">
              {type === 'image'
                ? (<span><FaImages /> Image Preview</span>)
                : type === 'audio' ? (<span><MdAudiotrack /> Audio Preview</span>)
                  : (<span><FaVideo /> Video Preview</span>)}
            </h5>
            <button
              onClick={onClose}
              className="btn btn-sm btn-light rounded-circle"
              style={{ width: '36px', height: '36px', padding: 0 }}
            >
              ✕
            </button>
          </div>

          <div className="p-4">
            <p className="text-muted small mb-3">{title}</p>
            {type === 'image' ? (
              <img
                src={url}
                alt="Preview"
                className="img-fluid rounded"
                style={{
                  maxHeight: '400px',
                  width: '100%',
                  objectFit: 'contain',
                }}
              />
            ) : type === 'audio' ? (
              <audio controls className="w-100">
                <source src={url} type="audio/mpeg" />
                Your browser does not support the audio element.
              </audio>
            ) : (
              <video controls autoPlay loop muted className="video-preview">
                <source src={url} type="video/mp4" />
                Your browser does not support the video element.
              </video>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// Preview Button - Updated for video support
const PreviewButton = ({ type, url, title, onPreview }) => {
  if (!url) return null;

  const getIcon = () => {
    switch (type) {
      case 'image':
        return <FaImages />;
      case 'audio':
        return <MdAudiotrack />;
      case 'video':
        return <FaVideo />;
      default:
        return <FaEye />;
    }
  };

  return (
    <button
      className="btn btn-sm btn-outline-primary ms-2"
      onClick={() => onPreview(type, url, title)}
      style={{
        padding: '5px 12px',
        fontSize: '13px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        height: '44px',
      }}
    >
      <span style={{ fontSize: '14px' }}>{getIcon()}</span>
      <span>View</span>
    </button>
  );
};

// Helper function to extract filename from URL
const getFileNameFromUrl = url => {
  if (!url) return '';
  const urlParts = url.split('/');
  const fileNameWithParams = urlParts[urlParts.length - 1];
  const fileName = fileNameWithParams.split('?')[0];
  return decodeURIComponent(fileName);
};


const CustomFileInput = ({
  fieldName,
  index,
  fileType,
  accept,
  disabled,
  onChange,
  existingFileUrl,
  originalFileName,
  placeholder = 'Choose file...',
  fileLabel = 'File',

  onFileValidationError,
  validateBeforeUpload,
}) => {
  const [fileName, setFileName] = useState(
    originalFileName || (existingFileUrl ? getFileNameFromUrl(existingFileUrl) : '')
  );

  const handleFileChange = e => {
    const file = e.target.files[0];

    if (file) {
      if (validateBeforeUpload && !validateBeforeUpload()) {
        e.target.value = '';
        setFileName('');
        if (onFileValidationError) {
          onFileValidationError();
        }
        return;
      }

      setFileName(file.name);
    } else {
      setFileName(originalFileName || (existingFileUrl ? getFileNameFromUrl(existingFileUrl) : ''));
    }
    onChange(e);
  };

  const handleClearFile = () => {
    setFileName('');
    const syntheticEvent = {
      target: {
        files: [null],
        value: '',
      },
    };
    onChange(syntheticEvent);
  };

  const getFileIcon = () => {
    if (accept?.includes('image')) return <FaImages />;
    if (accept?.includes('audio')) return <MdAudiotrack />;
    if (accept?.includes('video')) return <FaVideo />;
    return '📄';
  };

  return (
    <div className="file-input-wrapper">
      <div className="custom-file-input-container">
        <input
          type="file"
          accept={accept}
          onChange={handleFileChange}
          className="form-control form-control-sm modern-input"
          disabled={disabled}
          style={{
            fontSize: '13px',
            padding: '8px 12px',
            display: 'none',
          }}
          id={`file-${fieldName}-${index}-${fileType}`}
        />

        <div className={`custom-file-display ${fileName ? 'has-file' : ''}`}>
          <label htmlFor={`file-${fieldName}-${index}-${fileType}`} className="custom-file-label">
            <span
              className={`file-name ${fileName ? 'has-file' : ''}`}
              title={fileName || placeholder}
            >
              <span style={{ marginRight: '8px' }}>{getFileIcon()}</span>
              {fileName || placeholder}
            </span>
            <span className="browse-btn">Browse</span>
          </label>

          {fileName && (
            <a className="clear-file-btn" onClick={handleClearFile} title="Clear file">
              ✕
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

// OpenAI Model Dropdown Component
const OpenAIModelDropdown = ({ value, onChange, disabled = false }) => {
  return (
    <select
      value={value || ''}
      onChange={onChange}
      disabled={disabled}
      className="form-control modern-input"
      style={{
        fontSize: '14px',
        padding: '10px 14px',
        borderRadius: '10px',
        border: '2px solid #e9ecef',
        width: '100%',
      }}
    >
      <option value="">Select OpenAI Model</option>
      {ALL_OPENAI_MODELS.map(option => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
};

// Main Component - Updated for avatar looping video
export const MultipleApiKeysSettings = ({
  saveKeys,
  formData = {
    heygen_api_keys: [],
    avatar_api_keys: [],
    voice_api_keys: [],
    open_api_keys: [],
  },
  handleArrayChange,
  handleRemoveField,
  handleAddField,
  handleFileUpload,
  uploadingFiles = {},
  showErrorToast,
}) => {
  const [previewModal, setPreviewModal] = useState({
    isOpen: false,
    type: null,
    url: null,
    title: null,
  });

  const validateApiKeyFields = (fieldName, index) => {
    const item = formData[fieldName][index];
    const isNameEmpty = !item.name || item.name.trim() === '';
    const isApiKeyEmpty = !item.api_key || item.api_key.trim() === '';

    return {
      isValid: !isNameEmpty && !isApiKeyEmpty,
      isNameEmpty,
      isApiKeyEmpty,
    };
  };

  // In MultipleApiKeysSettings component, update the handleFileChange function:
  const handleFileChange = async (fieldName, index, fileType, event) => {
    const file = event.target.files[0];
    if (file) {
      const validation = validateApiKeyFields(fieldName, index);

      if (!validation.isValid) {
        let errorMessage = 'Please fill in ';
        const missingFields = [];

        if (validation.isNameEmpty) missingFields.push('API Key Name');
        if (validation.isApiKeyEmpty) missingFields.push('API Key Value');

        errorMessage += missingFields.join(' and ') + ' before uploading a file';

        if (showErrorToast) {
          alert(errorMessage);
        } else {
          alert(errorMessage);
        }

        // Reset the file input
        event.target.value = '';
        return;
      }

      await handleFileUpload(fieldName, index, fileType, file);
      event.target.value = '';
    }
  };

  const handlePreview = (type, url, title) => {
    setPreviewModal({ isOpen: true, type, url, title });
  };

  const closePreview = () => setPreviewModal({ isOpen: false, type: null, url: null, title: null });

  const multipleApiInputsArray = [
    {
      label: 'Heygen API Keys',
      name: 'heygen_api_keys',
      placeholder: 'Enter Heygen API Key',
      fileUpload: false,
      icon: <FaRegFileVideo />,
      color: '#6366f1',
    },
    {
      label: 'Avatar Keys',
      name: 'avatar_api_keys',
      placeholder: 'Enter Avatar Key',
      fileUpload: true,
      fileTypes: [
        {
          type: 'avatarImage',
          accept: 'image/*',
          label: 'Avatar Image',
          previewType: 'image',
        },
        {
          type: 'avatarLoopVideo',
          accept: 'video/*',
          label: 'Looping Video',
          previewType: 'video',
        },
      ],
      icon: <RxAvatar />,
      color: '#8b5cf6',
    },
    {
      label: 'Voice Keys',
      name: 'voice_api_keys',
      placeholder: 'Enter Voice Key',
      fileUpload: true,
      fileTypes: [
        {
          type: 'voiceKeyAudioSample',
          accept: 'audio/*',
          label: 'Voice Sample',
          previewType: 'audio',
        },
      ],
      icon: <MdOutlineKeyboardVoice />,
      color: '#ec4899',
    },
    {
      label: 'OpenAI API Keys',
      name: 'open_api_keys',
      placeholder: 'Enter OpenAI API Key',
      fileUpload: false,
      icon: <FaRobot />,
      color: '#10b981',
      hasModelDropdown: true,
    },
    {
      label: 'Weather API Keys',
      name: 'weather_api_keys',
      placeholder: 'Enter OpenWeatherMap API Key',
      fileUpload: false,
      icon: <WiDaySunny />,
      color: '#f59e0b',
    },
  ];

  return (
    <>
      <style>{`
        .modern-api-card {
          border: none;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
        }
        
        .gradient-header {
          background: #142127;
          padding: 30px;
        }
        
        .header-icon-wrapper {
          width: 56px;
          height: 56px;
          background: rgba(255, 255, 255, 0.25);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
        }
        
        .api-section {
          transition: all 0.3s ease;
          margin-bottom: 40px;
        }
        
        .section-badge {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 8px 18px;
          border-radius: 25px;
          font-size: 15px;
          font-weight: 600;
          color: white;
          margin-bottom: 20px;
        }
        
        .count-badge {
          background: white;
          color: #333;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
          margin-left: 4px;
        }
        
        .key-item-card {
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: 16px;
          padding: 20px;
          margin-bottom: 16px;
          transition: all 0.3s ease;  
        }
        
        .modern-input-label {
          font-size: 12px;
          color: #000;
          font-weight: 600;
          margin-bottom: 6px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .modern-input {
          border: 2px solid #e9ecef;
          border-radius: 10px;
          padding: 10px 14px;
          transition: all 0.3s ease;
          font-size: 14px;
          width: 100%;
        }
        
        .modern-input:focus {
          outline: none;
          border-color: #6366f1;
          box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1);
        }
        
        .file-upload-container {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          
        }
        
        .file-input-wrapper {
          flex: 1;
        }
        
        .custom-file-input-container {
          position: relative;
        }
        
        .custom-file-display {
          display: flex;
          align-items: center;
          border: 2px solid #e9ecef;
          border-radius: 10px;
          background: white;
          overflow: hidden;
          transition: all 0.3s ease;
          min-height: 38px;
        }
        
        .custom-file-display.has-file {
          border-color: #28a745;
          background: #f8fff9;
        }
        
        .custom-file-display:hover {
          border-color: #6366f1;
        }
        
        .custom-file-label {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex: 1;
          padding: 8px 12px;
          cursor: pointer;
          font-size: 13px;
          margin: 0;
        }
        
        .file-name {
          flex: 1;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #f3f4f6;
          border-radius: 8px;
          padding: 6px 10px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          color: #1f2937;
          font-weight: 500;
          font-size: 13px;
        }
        
        .file-name::before {
          content: "";
          flex-shrink: 0;
          font-size: 14px;
        }
        
        .browse-btn {
          color: white;
          padding: 5px 14px;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 600;
          margin-left: 10px;
          transition: all 0.3s ease;
        }
        
        .clear-file-btn {
          background: transparent;
          border: none;
          color: #9ca3af;
          font-size: 14px;
          margin-left: 6px;
          transition: all 0.2s ease;
        }
        
        .clear-file-btn:hover {
          color: #ef4444;
          transform: scale(1.1);
        }

        .add-btn {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;
          color: white;
          padding: 10px 24px;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          transition: all 0.3s ease;
          cursor: pointer;
        }
        
        .add-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
        }
        
        .delete-btn {
          background: #fef2f2;
          border: 2px solid #fee2e2;
          color: #ef4444;
          padding: 8px 16px;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 600;
          transition: all 0.3s ease;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          width: 100%;
        }
        
        .delete-btn:hover {
          background: #ef4444;
          color: white;
          border-color: #ef4444;
        }
        
        .save-btn {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;
          color: white;
          padding: 14px 36px;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 600;
          transition: all 0.3s ease;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        
        .save-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 30px rgba(102, 126, 234, 0.4);
        }
        
        .divider-line {
          height: 1px;
          background: linear-gradient(to right, transparent, #dee2e6, transparent);
          margin: 30px 0;
        }

        .file-upload-section {
          margin-bottom: 16px;
        }

        .file-upload-section:last-child {
          margin-bottom: 0;
        }

        @media (max-width: 768px) {
          .key-item-card .row > div {
            margin-bottom: 15px;
          }
          
          .custom-file-label {
            font-size: 12px;
            padding: 6px 10px;
          }
          
          .browse-btn {
            padding: 3px 8px;
            font-size: 11px;
          }
        }
      `}</style>

      <div className="modern-api-card">
        <div className="gradient-header">
          <div className="d-flex align-items-center gap-3">
            <div className="header-icon-wrapper"><FaKey /></div>
            <div>
              <h3 className="mb-1 text-white fw-bold" style={{ fontSize: '24px' }}>
                API Keys Manager
              </h3>
              <p className="mb-0 text-white" style={{ opacity: 0.9, fontSize: '14px' }}>
                Manage all your API keys and credentials in one place
              </p>
            </div>
          </div>
        </div>

        <div className="p-4">
          {multipleApiInputsArray.map((field, fieldIndex) => (
            <div key={field.name} className="api-section">
              <div className="section-badge" style={{ backgroundColor: field.color }}>
                <span style={{ fontSize: '18px' }}>{field.icon}</span>
                <span>{field.label}</span>
                <span className="count-badge">{formData[field.name]?.length || 0}</span>
              </div>

              {formData[field.name]?.map((item, idx) => {
                return (
                  <div key={idx} className="key-item-card">
                    <div className="row g-3 align-items-end">
                      {!field.fileUpload && (
                        <>
                          <div className="col-md-3 col-12">
                            <div className="modern-input-label">Key Name</div>
                            <input
                              type="text"
                              value={item.name || ''}
                              onChange={e =>
                                handleArrayChange(field.name, idx, 'name', e.target.value)
                              }
                              placeholder="Enter Api Key Name"
                              className="form-control modern-input"
                            />
                          </div>

                          <div className={`col-md-${field.name === 'heygen_api_keys' ? '5' : field.name === 'open_api_keys' ? '4' : '7'} col-12`}>

                            <div className="modern-input-label">API Key</div>
                            <input
                              type="text"
                              value={item.api_key || ''}
                              onChange={e =>
                                handleArrayChange(field.name, idx, 'api_key', e.target.value)
                              }
                              placeholder={field.placeholder}
                              className="form-control modern-input"
                            />
                          </div>

                          {field.name === 'heygen_api_keys' && (
                            <div className="col-md-2 col-12">
                              <div className="modern-input-label">High Quality</div>
                              <div
                                className="form-group custom-control custom-checkbox"
                                style={{ paddingLeft: '2.5em', margin: '10px 0px' }}
                              >
                                <input
                                  className="custom-control-input"
                                  type="checkbox"
                                  id={`high-quality-${field.name}-${idx}`}
                                  checked={item.enableHighQuality || false}
                                  onChange={e =>
                                    handleArrayChange(
                                      field.name,
                                      idx,
                                      'enableHighQuality',
                                      e.target.checked
                                    )
                                  }
                                  style={{
                                    width: '3em',
                                    height: '1.5em',
                                    cursor: 'pointer',
                                  }}
                                />
                                <label
                                  className="custom-control-label"
                                  htmlFor={`high-quality-${field.name}-${idx}`}
                                  style={{ cursor: 'pointer' }}
                                >
                                  Enable High Quality
                                </label>
                              </div>

                            </div>
                          )}


                          {/* OpenAI Model Dropdown - Only for OpenAI keys */}
                          {field.hasModelDropdown && (
                            <div className="col-md-3 col-12">
                              <div className="modern-input-label">OpenAI Model</div>
                              <OpenAIModelDropdown
                                value={item.model || ''}
                                onChange={e =>
                                  handleArrayChange(field.name, idx, 'model', e.target.value)
                                }
                              />
                            </div>
                          )}

                          <div className="col-md-2 col-12">
                            <button
                              type="button"
                              onClick={() => handleRemoveField(field.name, idx)}
                              className="delete-btn"
                            >
                              <Trash2 size={18} />
                              Remove
                            </button>
                          </div>
                        </>
                      )}

                      {field.fileUpload && (
                        <>
                          <div className="col-md-2 col-12">
                            <div className="modern-input-label">Key Name</div>
                            <input
                              type="text"
                              value={item.name || ''}
                              onChange={e =>
                                handleArrayChange(field.name, idx, 'name', e.target.value)
                              }
                              placeholder="Enter Api Key Name"
                              className="form-control modern-input"
                            />
                          </div>

                          <div className="col-md-4 col-12">
                            <div className="modern-input-label">API Key</div>
                            <input
                              type="text"
                              value={item.api_key || ''}
                              onChange={e =>
                                handleArrayChange(field.name, idx, 'api_key', e.target.value)
                              }
                              placeholder={field.placeholder}
                              className="form-control modern-input"
                            />
                          </div>

                          <div className="col-md-4 col-12">
                            {/* Render multiple file uploads for avatar keys */}
                            {field.fileTypes.map((fileTypeConfig, fileIndex) => {
                              const uploadKey = `${field.name}-${idx}-${fileTypeConfig.type}`;
                              const isUploading = uploadingFiles[uploadKey];
                              const hasFile = item[fileTypeConfig.type];

                              return (
                                <div key={fileTypeConfig.type} className="file-upload-section">
                                  <div className="modern-input-label">{fileTypeConfig.label}</div>
                                  <div className="file-upload-container">
                                    <CustomFileInput
                                      fieldName={field.name}
                                      index={idx}
                                      fileType={fileTypeConfig.type}
                                      accept={fileTypeConfig.accept}
                                      disabled={isUploading}
                                      onChange={e =>
                                        handleFileChange(field.name, idx, fileTypeConfig.type, e)
                                      }
                                      existingFileUrl={item[fileTypeConfig.type]}
                                      originalFileName={item[`${fileTypeConfig.type}FileName`]}
                                      placeholder={`Choose ${fileTypeConfig.label.toLowerCase()}...`}
                                      fileLabel={fileTypeConfig.label}
                                      // Add validation props
                                      validateBeforeUpload={() =>
                                        validateApiKeyFields(field.name, idx).isValid
                                      }
                                      onFileValidationError={() => {
                                        let errorMessage = 'Please fill in ';
                                        const missingFields = [];
                                        const validation = validateApiKeyFields(field.name, idx);

                                        if (validation.isNameEmpty)
                                          missingFields.push('API Key Name');
                                        if (validation.isApiKeyEmpty)
                                          missingFields.push('API Key Value');

                                        errorMessage +=
                                          missingFields.join(' and ') + ' before uploading a file';

                                        if (showErrorToast) {
                                          alert(errorMessage);
                                        } else {
                                          alert(errorMessage);
                                        }
                                      }}
                                    />
                                    {hasFile && (
                                      <PreviewButton
                                        type={fileTypeConfig.previewType}
                                        url={item[fileTypeConfig.type]}
                                        title={`${item.name} - ${fileTypeConfig.label}`}
                                        onPreview={handlePreview}
                                      />
                                    )}
                                  </div>
                                  {isUploading && (
                                    <small className="mt-2 d-block" style={{ color: 'grey' }}>
                                      ⏳ Uploading...
                                    </small>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          <div className="col-md-2 col-12">
                            <button
                              type="button"
                              onClick={() => handleRemoveField(field.name, idx)}
                              className="delete-btn"
                            >
                              <Trash2 size={18} />
                              Remove
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}

              <div className="text-end mt-3">
                <button
                  type="button"
                  className="add-btn"
                  onClick={() => handleAddField(field.name)}
                >
                  + Add {field.label.replace(/API Keys?|Keys?/gi, '').trim()}
                </button>
              </div>

              {fieldIndex < multipleApiInputsArray.length - 1 && (
                <div className="divider-line"></div>
              )}
            </div>
          ))}
        </div>
      </div>

      <PreviewModal
        isOpen={previewModal.isOpen}
        onClose={closePreview}
        type={previewModal.type}
        url={previewModal.url}
        title={previewModal.title}
      />
    </>
  );
};